import { openDB, type IDBPDatabase } from 'idb'

export interface ColumnState {
  _IsHidden: boolean
  _IsFrozen: boolean
}

export interface TableColumnStates {
  [columnId: string]: ColumnState
}

interface DatabaseColumnStates {
  [tableId: string]: TableColumnStates
}

const COLUMN_STATE_DB_NAME = 'makyo-column-states'
const COLUMN_STATE_STORE_NAME = 'table-column-states'
const COLUMN_STATE_DB_VERSION = 1

export class IndexedDBManager {
  private dbInstance: IDBPDatabase | null = null

  /**
   * Get the IndexedDB instance for column states
   */
  private async getColumnStateDB(): Promise<IDBPDatabase> {
    if (this.dbInstance) {
      return this.dbInstance
    }

    this.dbInstance = await openDB(COLUMN_STATE_DB_NAME, COLUMN_STATE_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(COLUMN_STATE_STORE_NAME)) {
          db.createObjectStore(COLUMN_STATE_STORE_NAME)
        }
      },
    })

    return this.dbInstance
  }

  /**
   * Save column state to IndexedDB
   */
  async saveColumnState(dbId: string, tableId: string, columnId: string, state: Partial<ColumnState>): Promise<void> {
    if (!dbId) return

    try {
      const db = await this.getColumnStateDB()

      // Get existing states for this database
      const existingStates: DatabaseColumnStates = (await db.get(COLUMN_STATE_STORE_NAME, dbId)) || {}

      // Ensure table structure exists
      if (!existingStates[tableId]) {
        existingStates[tableId] = {}
      }

      // Merge with existing state or create new
      const currentState = existingStates[tableId][columnId] || { _IsHidden: false, _IsFrozen: false }
      const newState = { ...currentState, ...state }

      // Update the state
      existingStates[tableId][columnId] = newState

      // Save back to IndexedDB
      await db.put(COLUMN_STATE_STORE_NAME, existingStates, dbId)
    } catch (error) {
      console.error('Failed to save column state:', error)
    }
  }

  /**
   * Update column states for a specific table
   */
  async updateColumnStates(dbId: string, tableId: string, columnStates: TableColumnStates): Promise<void> {
    if (!dbId) return

    try {
      const db = await this.getColumnStateDB()
      const existingStates: DatabaseColumnStates = (await db.get(COLUMN_STATE_STORE_NAME, dbId)) || {}
      existingStates[tableId] = columnStates
      await db.put(COLUMN_STATE_STORE_NAME, existingStates, dbId)
    } catch (error) {
      console.error('Failed to update column states:', error)
    }
  }

  /**
   * Load column states from IndexedDB
   */
  async loadColumnStates(dbId: string, tableId: string): Promise<TableColumnStates> {
    if (!dbId) return {}

    try {
      const db = await this.getColumnStateDB()

      const allStates: DatabaseColumnStates = await db.get(COLUMN_STATE_STORE_NAME, dbId)
      return allStates?.[tableId] || {}
    } catch (error) {
      console.error('Failed to load column states:', error)
      return {}
    }
  }

  /**
   * Clear column states for a specific table from IndexedDB
   */
  async clearColumnStates(dbId: string, tableId: string): Promise<void> {
    if (!dbId) return

    try {
      const db = await this.getColumnStateDB()

      const allStates: DatabaseColumnStates = await db.get(COLUMN_STATE_STORE_NAME, dbId)
      if (allStates?.[tableId]) {
        delete allStates[tableId]
        await db.put(COLUMN_STATE_STORE_NAME, allStates, dbId)
      }
    } catch (error) {
      console.error('Failed to clear column states:', error)
    }
  }

  /**
   * Clear all column states for a database
   */
  async clearAllColumnStates(dbId: string): Promise<void> {
    if (!dbId) return

    try {
      const db = await this.getColumnStateDB()
      await db.delete(COLUMN_STATE_STORE_NAME, dbId)
    } catch (error) {
      console.error('Failed to clear all column states:', error)
    }
  }

  /**
   * Close the database connection
   */
  async close(): Promise<void> {
    if (this.dbInstance) {
      this.dbInstance.close()
      this.dbInstance = null
    }
  }
}
